import * as React from 'react';
import {
  t as translate,
  formatCzechDate,
  formatCzechDateTime,
  formatCzechMonth,
  formatCzechTime,
  isSettledCzechTemporalInput,
  parseCzechDate,
  parseCzechDateTime,
  parseCzechMonth,
  parseCzechTime,
} from '@reos/i18n';
import { useLocaleOrDefault } from '../lib/i18n.js';

/*
 * Krok UXF3 — JEDNA komponenta pro datum a cas v cele administraci (#44, pravidlo 5).
 *
 * Proc ne `<input type="date">`: Chromium kresli nativni pole podle lokalizace
 * OPERACNIHO SYSTEMU, takze cesky ucet vidi `mm/dd/yyyy` a `05:34 AM` a aplikace
 * to nijak neprebije (ani `lang="cs"`, ani Playwright `locale`). Audit chce
 * jednoznacnou ZOBRAZENOU hodnotu, proto se zadava text v ceskem zapisu.
 *
 * Hodnota navenek (`value` / `onValueChange`) zustava STROJOVA a shodna s tim,
 * co posilalo nativni pole (`YYYY-MM-DD`, `YYYY-MM-DDTHH:mm`, `HH:mm`, `YYYY-MM`),
 * takze volajici obrazovky ani API se nemenily. `parse*` prijima i ISO zapis,
 * aby stare testy a odkazy s `?from=2026-10-01` fungovaly dal.
 *
 * Hlidac `date-field-guard.spec.ts` zakazuje nativni `type="date"` jinde —
 * vyjimka je na SYMBOL teto komponenty, ne na soubor (CLAUDE.md §8 bod 3).
 */

type FieldKind = 'date' | 'datetime' | 'time' | 'month';

const CONVERTERS: Record<
  FieldKind,
  { readonly format: (value: string | null | undefined) => string; readonly parse: (input: string) => string | null }
> = {
  date: { format: formatCzechDate, parse: parseCzechDate },
  datetime: { format: formatCzechDateTime, parse: parseCzechDateTime },
  time: { format: formatCzechTime, parse: parseCzechTime },
  month: { format: formatCzechMonth, parse: parseCzechMonth },
};

export interface DateFieldProps {
  /** Strojova hodnota (`YYYY-MM-DD` / `YYYY-MM-DDTHH:mm` / `HH:mm` / `YYYY-MM`), `''` = nevyplneno. */
  readonly value: string;
  /** Dostane strojovou hodnotu, nebo `''` kdyz uzivatel pole vyprazdnil. */
  readonly onValueChange: (value: string) => void;
  readonly id?: string | undefined;
  readonly className?: string | undefined;
  readonly required?: boolean | undefined;
  readonly disabled?: boolean | undefined;
  readonly autoFocus?: boolean | undefined;
  readonly ariaLabel?: string | undefined;
  readonly testId?: string | undefined;
}

interface InnerProps extends DateFieldProps {
  readonly kind: FieldKind;
}

function CzechTemporalField({
  kind,
  value,
  onValueChange,
  id,
  className,
  required,
  disabled,
  autoFocus,
  ariaLabel,
  testId,
}: InnerProps): React.JSX.Element {
  const locale = useLocaleOrDefault();
  const t = (key: Parameters<typeof translate>[0]): string => translate(key, { locale });
  const { format, parse } = CONVERTERS[kind];
  const [draft, setDraft] = React.useState(() => format(value));
  /*
   * Synchronizace s props se dela V RENDERU, ne v `useEffect`: efekt po
   * pripojeni se v plne brane stiha az po prvnim mereni a pole pak ukaze
   * prazdnou hodnotu (poucenim z UXK3/contracts-draft-input).
   */
  const [seenValue, setSeenValue] = React.useState(value);
  /*
   * Krok NOC2 bod 1 (nalez N07): DOKUD V POLI STOJI KURZOR, HODNOTA ZE SERVERU
   * ROZEPSANY TEXT NEPREPISUJE. Editor smlouvy posila kazde ulozeni jako PATCH
   * cele smlouvy a odpovedi chodi v jinem poradi, nez se odeslaly — starsi
   * odpoved tak uprostred psani prepsala pole (QA videlo v poli jen „6")
   * a dalsi stisk klavesy z toho udelal rok `20026`. Tentyz zavod resi
   * `DraftTextInput` v editoru stejnym pravidlem.
   */
  const focused = React.useRef(false);
  /** Posledni hodnota, kterou TOHLE pole poslalo volajicimu (proti dvojimu ulozeni). */
  const sent = React.useRef(value);
  if (value !== seenValue) {
    setSeenValue(value);
    if (!focused.current && parse(draft) !== value) setDraft(format(value));
  }

  const invalid = draft.trim() !== '' && parse(draft) === null;

  function handleChange(next: string): void {
    setDraft(next);
    const parsed = parse(next);
    if (parsed !== null) {
      /*
       * Krok NOC2 bod 1: pri psani se uklada jen HOTOVY zapis. Prefix delsiho
       * roku (`1. 11. 20`) je taky platne datum, takze se do NOC2 ulozilo
       * behem jednoho zadani nekolik ruznych hodnot; zkraceny tvar
       * (`10.10.26`) se ulozi az pri odchodu z pole (`handleBlur`).
       */
      if (!isSettledCzechTemporalInput(next, format(parsed), parsed)) return;
      commit(parsed);
    } else if (next.trim() === '') {
      commit('');
    }
  }

  function commit(next: string): void {
    sent.current = next;
    setSeenValue(next);
    onValueChange(next);
  }

  /** Po odchodu z pole se platna hodnota prepise na kanonicky zapis (`10.10.26` → `10. 10. 2026`). */
  function handleBlur(): void {
    focused.current = false;
    const parsed = parse(draft);
    if (parsed === null) return;
    setDraft(format(parsed));
    // Zkraceny zapis se pri psani zamerne neukladal — ulozi se tady.
    if (parsed !== sent.current) commit(parsed);
  }

  return (
    <input
      type="text"
      inputMode="numeric"
      autoComplete="off"
      lang="cs"
      id={id}
      className={className}
      required={required}
      disabled={disabled}
      autoFocus={autoFocus}
      aria-label={ariaLabel}
      aria-invalid={invalid ? true : undefined}
      placeholder={t(`webAdmin.dateField.placeholder.${kind}`)}
      title={t(`webAdmin.dateField.hint.${kind}`)}
      data-testid={testId}
      data-date-field={kind}
      value={draft}
      onChange={(e) => handleChange(e.target.value)}
      onFocus={() => {
        focused.current = true;
      }}
      onBlur={handleBlur}
    />
  );
}

/** Datum `10. 10. 2026`; hodnota `YYYY-MM-DD`. */
export function DateField(props: DateFieldProps): React.JSX.Element {
  return <CzechTemporalField kind="date" {...props} />;
}

/** Datum a cas `10. 10. 2026 05:34`; hodnota `YYYY-MM-DDTHH:mm`. */
export function DateTimeField(props: DateFieldProps): React.JSX.Element {
  return <CzechTemporalField kind="datetime" {...props} />;
}

/** Cas ve 24 h `05:34`; hodnota `HH:mm`. */
export function TimeField(props: DateFieldProps): React.JSX.Element {
  return <CzechTemporalField kind="time" {...props} />;
}

/** Mesicni obdobi `10. 2026`; hodnota `YYYY-MM`. */
export function MonthField(props: DateFieldProps): React.JSX.Element {
  return <CzechTemporalField kind="month" {...props} />;
}
